/**
 * **The full-set player, at the bottom of the whole app** (the QA walk, w-sets-play, 2026-11-02: "on live mode, the
 * schedule or anywhere in the app, while playing a full set, run the player at the bottom of the app, with play,
 * stop etc., and let the user carry on using the app with the set playing in the background after closing the artist
 * page"; w-sets-list: "it opens on YouTube and not in the app").
 *
 * ONE player for the app, mounted once above every screen (`App.tsx`): a set's ▶ (Full sets, a set's card, an
 * artist's page) hands it the recording and the screen can close — the set keeps playing in the bar, which has the
 * title, the artist, play / pause, stop, a bigger / smaller picture and close. Nothing reaches YouTube, SoundCloud or
 * Mixcloud before ▶ (the bar does not exist until then). Play / pause / stop talk to the platform's own embed by
 * `postMessage` (YouTube's `enablejsapi`, SoundCloud's and Mixcloud's widget messages); close removes the player.
 * With no connection ▶ plays nothing: the caller shows "You're offline" (`OfflineMusicPop`).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { embedUrl, type RecordingPlatform } from '@myrave/shared';
import { useT } from '@/i18n';
import { trackTitle, useTrackInfo } from '@/lib/id3Title';
import { soundcloudPageOf } from '@/lib/sample';
import { LiveWave } from './LiveWave';
import './SetPlayer.css';

export interface PlayingSet {
  id: string;
  platform: RecordingPlatform;
  externalId: string;
  title: string;
  /** Who plays it — the artist's name. */
  artist: string;
  /** The artist's picture, shown in the bar. */
  photo?: string | null;
}

/**
 * A set's SAMPLE (its short preview), played by the same bar (the user, 2026-11-02: "when playing a sample or a full
 * set in the artist panel, keep the player in a second footer on the schedule … don't stop playing if the user is
 * switching pages or artists; stop only if the user pressed stop or left the rave app — same on mobile"). ONE audio
 * element for the app: the panel's player, the schedule's and My lineup's ▶ all drive it.
 */
export interface PlayingSample {
  /** The set's slug — which row's ▶ shows "playing". */
  key: string;
  src: string;
  /** Who plays it — the artist's name. */
  artist: string;
  /** The artist's picture, shown in the bar (the user, 2026-11-02). */
  photo?: string | null;
}

interface SetPlayerState {
  /** Where the full set is and how long it is, in seconds, as its embed reports them (YouTube, SoundCloud). */
  setAt: number;
  setLength: number;
  seekSet: (seconds: number) => void;
  /** The embed is ready: ask it to report its position. */
  listen: () => void;
  /** The video did not start by itself: it waits for a tap on the picture (a phone). */
  needsTap: boolean;
  /** The sample's track title, from its own tags (`lib/id3Title.ts`); null while unknown or when it has none. */
  sampleTitle: string | null;
  playing: PlayingSet | null;
  paused: boolean;
  play: (set: PlayingSet) => void;
  toggle: () => void;
  stop: () => void;
  close: () => void;
  sample: PlayingSample | null;
  samplePaused: boolean;
  /** Where the sample is and how long it is, in seconds. */
  at: number;
  length: number;
  muted: boolean;
  /** Bumped when a sample could not play (no connection, a broken file) — a caller says so. */
  failed: number;
  /** Plays this sample — or, when it is the one already loaded, plays or pauses it. Must be called from a press. */
  playSample: (sample: PlayingSample) => void;
  seek: (seconds: number) => void;
  setMuted: (muted: boolean) => void;
  /** Stop the sample and take the bar away. */
  stopSample: () => void;
}

const NONE: SetPlayerState = {
  playing: null, paused: false, play: () => {}, toggle: () => {}, stop: () => {}, close: () => {},
  setAt: 0, setLength: 0, seekSet: () => {}, listen: () => {}, needsTap: false,
  sampleTitle: null, sample: null, samplePaused: true, at: 0, length: 0, muted: false, failed: 0,
  playSample: () => {}, seek: () => {}, setMuted: () => {}, stopSample: () => {},
};
const Ctx = createContext<SetPlayerState>(NONE);

/** The app's one full-set player. Outside its provider (a test of one screen) it plays nothing. */
export function useSetPlayer(): SetPlayerState {
  return useContext(Ctx);
}

/** No connection: the browser's own word. A ▶ then plays nothing and says so. */
export function noConnection(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export const PLATFORM_NAMES: Record<string, string> = { YOUTUBE: 'YouTube', SOUNDCLOUD: 'SoundCloud', MIXCLOUD: 'Mixcloud' };

/** The embed's address — YouTube's with its control messages switched on. Exported for the test. */
export function playerSrc(set: Pick<PlayingSet, 'platform' | 'externalId'>): string | null {
  const src = embedUrl(set.platform, set.externalId);
  // `controls=0`: no pause of its own on the picture — the bar's is the one (the user, 2026-11-02).
  return src && set.platform === 'YOUTUBE' ? `${src}&enablejsapi=1&playsinline=1&controls=0` : src;
}

/** How long YouTube may take to start by itself before the video asks for a tap. Exported for the test. */
export const TAP_AFTER_MS = 2500;

const ORIGIN: Record<string, string> = {
  YOUTUBE: 'https://www.youtube-nocookie.com',
  SOUNDCLOUD: 'https://w.soundcloud.com',
  MIXCLOUD: 'https://player-widget.mixcloud.com',
};

type Command = 'play' | 'pause' | 'stop';

/** The message each platform's embed takes for a command. Exported for the test. */
export function commandMessages(platform: RecordingPlatform, command: Command): string[] {
  if (platform === 'YOUTUBE') {
    const func = { play: 'playVideo', pause: 'pauseVideo', stop: 'stopVideo' }[command];
    return [JSON.stringify({ event: 'command', func, args: [] })];
  }
  if (platform === 'SOUNDCLOUD') {
    if (command === 'stop') return [JSON.stringify({ method: 'pause' }), JSON.stringify({ method: 'seekTo', value: 0 })];
    return [JSON.stringify({ method: command })];
  }
  if (platform === 'MIXCLOUD') {
    const call = (methodName: string, args: unknown[] = []) => JSON.stringify({ mixcloud: 'playerWidget', type: 'method', data: { methodName, args } });
    if (command === 'stop') return [call('pause'), call('seek', [0])];
    return [call(command)];
  }
  return [];
}

export function SetPlayerProvider({ children }: { children: ReactNode }) {
  const [playing, setPlaying] = useState<PlayingSet | null>(null);
  const [paused, setPaused] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  const send = useCallback((command: Command) => {
    const win = frame.current?.contentWindow;
    if (!win || !playing) return;
    for (const m of commandMessages(playing.platform, command)) win.postMessage(m, ORIGIN[playing.platform] ?? '*');
  }, [playing]);

  // The sample's one audio element. Its `src` is set by hand, inside the press, so `play()` keeps the press's permission.
  const audio = useRef<HTMLAudioElement>(null);
  const [sample, setSample] = useState<PlayingSample | null>(null);
  const [samplePaused, setSamplePaused] = useState(true);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);
  const [muted, setMuted] = useState(false);
  const [failed, setFailed] = useState(0);
  const [sampleTitle, setSampleTitle] = useState<string | null>(null);
  useEffect(() => {
    setSampleTitle(null);
    if (!sample) return undefined;
    let live = true;
    void trackTitle(sample.src).then((t) => { if (live) setSampleTitle(t); });
    return () => { live = false; };
  }, [sample]);
  const endSample = useCallback(() => {
    const el = audio.current;
    if (el?.getAttribute('src')) { el.pause(); el.removeAttribute('src'); }
    setSample(null); setSamplePaused(true); setAt(0); setLength(0);
  }, []);
  const playSample = useCallback((next: PlayingSample) => {
    const el = audio.current;
    if (!el) return;
    if (sample?.src === next.src) {
      // The player's own state decides, never the element's — every button shows that state.
      if (samplePaused) void el.play().catch(() => { setSamplePaused(true); setFailed((n) => n + 1); });
      else el.pause();
      return;
    }
    setPlaying(null); // one thing plays at a time: a sample ends the full set
    el.src = next.src;
    setSample(next); setAt(0); setLength(0); setSamplePaused(false);
    void el.play().catch(() => { setSamplePaused(true); setFailed((n) => n + 1); });
  }, [sample, samplePaused]);
  const seek = useCallback((seconds: number) => { if (audio.current) audio.current.currentTime = seconds; }, []);

  const play = useCallback((set: PlayingSet) => {
    if (noConnection() || !playerSrc(set)) return;
    endSample(); // …and a full set ends the sample
    setPlaying((now) => (now?.id === set.id ? now : set));
    setPaused(false);
  }, [endSample]);
  const toggle = useCallback(() => {
    setPaused((p) => { send(p ? 'play' : 'pause'); return !p; });
  }, [send]);
  const stop = useCallback(() => { send('stop'); setPaused(true); }, [send]);
  const close = useCallback(() => { setPlaying(null); setPaused(false); }, []);

  // The full set's position, from its embed's own messages; seeking through them too.
  const [fullAt, setFullAt] = useState(0);
  const [fullLength, setFullLength] = useState(0);
  /*
   * A phone that will not start the video by itself (the user, 2026-11-03: "YouTube videos not playing"): a web view or
   * a phone's browser refuses autoplay with sound, and the cover over the video took the tap YouTube waits for. So when
   * YouTube has not started `TAP_AFTER_MS` after loading, the cover goes and the video asks to be tapped; it comes back
   * once the video plays.
   */
  const [needsTap, setNeedsTap] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    setFullAt(0); setFullLength(0); setNeedsTap(false); started.current = false;
    if (!playing) return undefined;
    const origin = ORIGIN[playing.platform];
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== frame.current?.contentWindow) return;
      let d: { info?: { currentTime?: number; duration?: number; playerState?: number }; method?: string; value?: unknown } | null = null;
      try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch { return; }
      if (!d) return;
      if (playing.platform === 'YOUTUBE' && d.info) {
        // 1 playing, 3 buffering: it started — the cover is back.
        if (d.info.playerState === 1 || d.info.playerState === 3) { started.current = true; setNeedsTap(false); }
        if (typeof d.info.currentTime === 'number') setFullAt(d.info.currentTime);
        if (typeof d.info.duration === 'number' && d.info.duration > 0) setFullLength(d.info.duration);
      }
      if (playing.platform === 'SOUNDCLOUD') {
        const v = d.value as { currentPosition?: number; relativePosition?: number } | number | undefined;
        if (d.method === 'playProgress' && v && typeof v === 'object' && typeof v.currentPosition === 'number') {
          setFullAt(v.currentPosition / 1000);
          if (v.relativePosition) setFullLength(v.currentPosition / 1000 / v.relativePosition);
        }
        if (d.method === 'getDuration' && typeof v === 'number') setFullLength(v / 1000);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [playing]);
  const post = useCallback((message: unknown) => {
    const win = frame.current?.contentWindow;
    if (win && playing) win.postMessage(JSON.stringify(message), ORIGIN[playing.platform] ?? '*');
  }, [playing]);
  const listen = useCallback(() => {
    if (!playing) return;
    const ask = () => {
      if (playing.platform === 'YOUTUBE') post({ event: 'listening', id: playing.id, channel: 'widget' });
      if (playing.platform === 'SOUNDCLOUD') { post({ method: 'addEventListener', value: 'playProgress' }); post({ method: 'getDuration' }); }
    };
    ask();
    setTimeout(ask, 1200); // YouTube answers once its player is up
    if (playing.platform === 'YOUTUBE') setTimeout(() => { if (!started.current) setNeedsTap(true); }, TAP_AFTER_MS);
  }, [playing, post]);
  const seekSet = useCallback((seconds: number) => {
    if (!playing) return;
    if (playing.platform === 'YOUTUBE') post({ event: 'command', func: 'seekTo', args: [seconds, true] });
    if (playing.platform === 'SOUNDCLOUD') post({ method: 'seekTo', value: Math.round(seconds * 1000) });
    if (playing.platform === 'MIXCLOUD') post({ mixcloud: 'playerWidget', type: 'method', data: { methodName: 'seek', args: [seconds] } });
    setFullAt(seconds);
  }, [playing, post]);

  const value = useMemo(() => ({
    playing, paused, play, toggle, stop, close, setAt: fullAt, setLength: fullLength, seekSet, listen, needsTap,
    sampleTitle, sample, samplePaused, at, length, muted, failed, playSample, seek, setMuted, stopSample: endSample,
  }), [playing, paused, play, toggle, stop, close, fullAt, fullLength, seekSet, listen, needsTap, sampleTitle, sample, samplePaused, at, length, muted, failed, playSample, seek, endSample]);
  const shown = Boolean(playing || sample);
  // While the bar is up the page leaves room for it (`SetPlayer.css`, `html[data-player]`).
  // The bar sits on the computer's footer (a second footer); `--player-foot` is that footer's height, measured.
  useEffect(() => {
    if (!shown) return undefined;
    const root = document.documentElement;
    root.setAttribute('data-player', '');
    const place = () => {
      const foot = document.querySelector<HTMLElement>('.shell-footer');
      const box = foot?.getBoundingClientRect();
      const showing = box && box.height > 0 && box.top < window.innerHeight;
      root.style.setProperty('--player-foot', `${showing ? Math.round(window.innerHeight - box.top) : 0}px`);
      // …and starts where the page does, beside the computer's menu, never under it.
      const main = document.querySelector<HTMLElement>('.shell-main')?.getBoundingClientRect();
      const rtl = document.documentElement.dir === 'rtl';
      const start = main ? Math.max(0, Math.round(rtl ? window.innerWidth - main.right : main.left)) : 0;
      root.style.setProperty('--player-start', `${start}px`);
    };
    place();
    // The docked artist panel measures its foot on resize: tell it the bar came (and, below, went).
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { passive: true, capture: true });
    // The menu opening or closing moves the page without a resize: watch the page's own box too.
    const main = document.querySelector('.shell-main');
    const watch = typeof ResizeObserver === 'undefined' || !main ? null : new ResizeObserver(place);
    if (main) watch?.observe(main);
    return () => {
      watch?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, { capture: true });
      root.removeAttribute('data-player');
      root.style.removeProperty('--player-foot');
      root.style.removeProperty('--player-start');
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };
  }, [shown]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <audio
        ref={audio}
        preload="metadata"
        muted={muted}
        className="set-player-audio"
        onPlay={() => setSamplePaused(false)}
        onPause={() => setSamplePaused(true)}
        onEnded={() => { setSamplePaused(true); setAt(0); }}
        onError={() => { if (audio.current?.getAttribute('src')) { setSamplePaused(true); setFailed((n) => n + 1); } }}
        onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setLength(e.currentTarget.duration)}
      />
      {shown && typeof document !== 'undefined' && createPortal(<MiniPlayer frameRef={frame} />, document.body)}
    </Ctx.Provider>
  );
}

const ICON = {
  play: 'M8 5v14l11-7z',
  pause: 'M6 5h4v14H6zM14 5h4v14h-4z',
  stop: 'M6 6h12v12H6z',
  close: 'M6 6l12 12M18 6L6 18',
  bigger: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
};
function Icon({ d, stroke = false }: { d: string; stroke?: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false"
      fill={stroke ? 'none' : 'currentColor'} stroke={stroke ? 'currentColor' : 'none'} strokeWidth={stroke ? 2.2 : 0} strokeLinecap="round">
      <path d={d} />
    </svg>
  );
}

/**
 * A recording's title as the bar says it, under the artist's name (the user, 2026-11-02: "1st line Captain Hook, 2nd
 * line Ozora Festival 2026 — filter out [full video / 4K] — | platform"): the artist's name, emoji, and brackets that
 * only describe the upload are taken out. "Captain Hook 🌀 Ozora Festival 2026 [full video /4K]" → "Ozora Festival 2026".
 */
export function setTitleFor(title: string, artist: string): string {
  let t = title;
  if (artist.trim()) t = t.replace(new RegExp(artist.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ');
  t = t.replace(/[[(][^\])]*[\])]/g, ' ')            // [full video /4K], (Official), (Live)
    .replace(/\p{Extended_Pictographic}|\uFE0F/gu, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—|·:@,]+|[\s\-–—|·:@,]+$/g, '')
    .trim();
  return t;
}

/** m:ss — or h:mm:ss for a set of an hour or more — a track's position. */
function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const n = Math.floor(seconds);
  const ss = String(n % 60).padStart(2, '0');
  if (n >= 3600) return `${Math.floor(n / 3600)}:${String(Math.floor((n % 3600) / 60)).padStart(2, '0')}:${ss}`;
  return `${Math.floor(n / 60)}:${ss}`;
}

/**
 * Where the playing is: Live's own progress bar (`LiveWave` — the MyRave bars, in the rave's colours), the time played
 * before it and the length after it (the user, 2026-11-02: "show the times near the progress bar; use our progress bar
 * from the Live page"). A slider over it, unseen, seeks.
 */
function Progress({ at, length, onSeek }: { at: number; length: number; onSeek: (seconds: number) => void }) {
  const tr = useT();
  const pct = length ? Math.min(100, (at / length) * 100) : 0;
  return (
    <span className="set-player-progress">
      <span className="set-player-time" dir="ltr">{clock(at)}</span>
      <span className="set-player-wave">
        <LiveWave pct={pct} label={tr('Elapsed: {time} ({pct}%)', { time: clock(at), pct: pct.toFixed(0) })} />
        <input
          type="range" className="set-player-seek-hit" min={0} max={length || 0} step={0.5} value={Math.min(at, length || 0)}
          disabled={!length} aria-label={tr('Seek')} onChange={(e) => onSeek(Number(e.currentTarget.value))}
        />
      </span>
      <span className="set-player-time" dir="ltr">{length ? clock(length) : '–:––'}</span>
    </span>
  );
}

/** The sample in the bar: play / pause, the track and the artist, the progress, stop. */
function SampleBar() {
  const tr = useT();
  const { sample, sampleTitle, samplePaused, at, length, playSample, seek, stopSample } = useSetPlayer();
  // The track's name and its source, SoundCloud — as the artist panel says them (`useTrackInfo`, one ask for both).
  const info = useTrackInfo(sample?.src);
  if (!sample) return null;
  const soundcloudPage = info?.soundcloud ?? soundcloudPageOf(sample.src);
  return (
    <div className="set-player set-player--sample" role="region" aria-label={tr('{name} — player', { name: sample.artist })} data-print="hide">
      {/* The artist's photo IS the play / pause button (the user, 2026-11-02), the sign over it. */}
      <button type="button" className="set-player-btn is-main" onClick={() => playSample(sample)}
        data-photo={sample.photo ? '' : undefined} style={sample.photo ? { ['--photo' as string]: `url("${sample.photo}")` } : undefined}
        aria-label={samplePaused ? tr('Play {name}', { name: sample.artist }) : tr('Pause')}>
        <Icon d={samplePaused ? ICON.play : ICON.pause} />
      </button>
      <div className="set-player-text">
        {/* As a full set: the artist, then the track's own name (its tags, or SoundCloud's), else "Sample" — and the
            source on its own third line, small, as a full set's "YouTube" (the user, 2026-11-02): SoundCloud, a link to
            the track (its API terms). */}
        <span className="set-player-title">{sample.artist}</span>
        <span className="set-player-sub">{info?.title ?? sampleTitle ?? tr('Sample')}</span>
        {soundcloudPage && (
          <a className="set-player-sub set-player-platform set-player-credit" href={soundcloudPage} target="_blank" rel="noopener noreferrer">SoundCloud</a>
        )}
      </div>
      <Progress at={at} length={length} onSeek={seek} />
      <button type="button" className="set-player-btn" onClick={stopSample} aria-label={tr('Stop')}>
        <Icon d={ICON.stop} />
      </button>
    </div>
  );
}

function MiniPlayer({ frameRef }: { frameRef: React.RefObject<HTMLIFrameElement> }) {
  const tr = useT();
  const { playing, paused, toggle, close, sample, setAt, setLength, seekSet, listen, needsTap } = useSetPlayer();
  const [big, setBig] = useState(false);
  if (!playing) return sample ? <SampleBar /> : null;
  const src = playerSrc(playing);
  const platform = PLATFORM_NAMES[playing.platform] ?? tr('Other');
  // The sample's bar, exactly (the user, 2026-11-02: "the full set player should look the same, the video on the right
  // side of the progress bar"): the photo as play / pause, the set's name, the artist, platform and time, the bar,
  // the video, bigger, stop.
  return (
    <div className="set-player set-player--sample set-player--set" role="region" aria-label={tr('Full set player')} data-big={big || undefined} data-print="hide">
      <button type="button" className="set-player-btn is-main" onClick={toggle}
        data-photo={playing.photo ? '' : undefined} style={playing.photo ? { ['--photo' as string]: `url("${playing.photo}")` } : undefined}
        aria-label={paused ? tr('Play {name}', { name: playing.artist }) : tr('Pause')}>
        <Icon d={paused ? ICON.play : ICON.pause} />
      </button>
      <div className="set-player-text">
        {/* The artist; the set's name without the artist or the upload's words; where (the user, 2026-11-02: "put
            YouTube as the 3rd line"). */}
        <span className="set-player-title">{playing.artist}</span>
        {setTitleFor(playing.title, playing.artist) && <span className="set-player-sub">{setTitleFor(playing.title, playing.artist)}</span>}
        <span className="set-player-sub set-player-platform">{platform}</span>
      </div>
      <Progress at={setAt} length={setLength} onSeek={seekSet} />
      <div className="set-player-screen" data-audio={playing.platform !== 'YOUTUBE' || undefined} data-needs-tap={needsTap || undefined}>
        {/* Over the video, nothing of YouTube's own — no pause on the picture (the bar has it); pressed, the video
            fills the screen (the user, 2026-11-02). Not while the video waits for a tap to start (`needsTap`). */}
        {playing.platform === 'YOUTUBE' && !needsTap && (
          <button type="button" className="set-player-screen-hit" aria-label={tr('Full screen')}
            onClick={() => {
              const el = frameRef.current;
              if (el?.requestFullscreen) void el.requestFullscreen().catch(() => setBig(true));
              else setBig(true);
            }} />
        )}
        {src && (
          <iframe
            key={playing.id}
            ref={frameRef}
            src={src}
            onLoad={listen}
            title={tr('{name} — player', { name: playing.artist })}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
      </div>
      <button type="button" className="set-player-btn" onClick={() => setBig((b) => !b)} aria-pressed={big}
        aria-label={big ? tr('Smaller player') : tr('Bigger player')}>
        <Icon d={ICON.bigger} stroke />
      </button>
      <button type="button" className="set-player-btn" onClick={close} aria-label={tr('Stop')}>
        <Icon d={ICON.stop} />
      </button>
    </div>
  );
}
